import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService, User } from '../../core/auth/auth.service';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { take, filter, timeout } from 'rxjs/operators';
import { AnalyticsService } from '../../shared/services/analytics.service';
import { ThemeService } from '../../shared/services/theme.service';
import { ApiService } from '../../shared/services/api.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit {
  loginForm: FormGroup;
  loading = false;
  maintenanceModeActive = false;
  maintenanceMessage = '';
  isDarkMode$ = this.themeService.isDarkMode$;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private snackBar: MatSnackBar,
    private analyticsService: AnalyticsService,
    private themeService: ThemeService,
    private apiService: ApiService
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]]
    });
  }

  toggleTheme(): void {
    this.themeService.toggleDarkMode();
  }

  ngOnInit(): void {
    this.apiService.getPublicSystemStatus().subscribe({
      next: (status) => {
        this.maintenanceModeActive = status.maintenanceModeActive === true;
        this.maintenanceMessage =
          String(status.maintenanceMessage || '').trim() ||
          'System maintenance is currently in progress. Please try again shortly.';
      },
      error: () => {
        this.maintenanceModeActive = false;
        this.maintenanceMessage = '';
      },
    });

    // Redirect if already authenticated
    // Use take(1) to only check once and avoid race conditions during logout
    this.authService.getCurrentUser().pipe(
      take(1)
    ).subscribe(user => {
      if (user) {
        if (user.twoFactorEnabled && !this.authService.getTwoFactorSessionToken()) {
          void this.router.navigate(['/otp']);
          return;
        }
        void this.navigateByRole(user);
      }
    });
  }

  async onSubmit(): Promise<void> {
    if (this.maintenanceModeActive) {
      this.snackBar.open(this.maintenanceMessage || 'Login is temporarily unavailable due to maintenance.', 'Close', {
        duration: 5000,
      });
      return;
    }
    if (this.loginForm.valid) {
      this.loading = true;
      try {
        const email = this.loginForm.value.email as string;
        const password = this.loginForm.value.password as string;
        const loginResult = await this.authService.login(
          email,
          password
        );
        void this.storeCredentialForPasswordManagers(email, password);

        if (loginResult.requiresTwoFactor) {
          const twoFactor = await firstValueFrom(this.apiService.startTwoFactorChallenge());
          if (!twoFactor.required || !twoFactor.challengeId || !twoFactor.expiresAt || !twoFactor.resendAvailableAt) {
            throw new Error('Two-factor challenge could not be started');
          }
          this.authService.setPendingTwoFactorChallenge({
            challengeId: twoFactor.challengeId,
            expiresAt: twoFactor.expiresAt,
            resendAvailableAt: twoFactor.resendAvailableAt,
          });
          this.loading = false;
          await this.router.navigate(['/otp']);
          return;
        }
        
        // Wait for user observable to emit the logged-in user before navigating
        // This ensures AuthGuard will see the user when checking authentication
        this.authService.getCurrentUser().pipe(
          filter(user => user !== null),
          take(1),
          timeout(5000)
        ).subscribe({
          next: (user) => {
            this.loading = false;
            this.analyticsService.trackLoginSucceeded(user?.role);
            // Login is now logged by the backend API on first API call after authentication
            void this.navigateByRole(user);
          },
          error: () => {
            // If timeout or error, try navigating anyway (user might still be logged in)
            this.loading = false;
            // Login is now logged by the backend API on first API call after authentication
            this.authService.getCurrentUser().pipe(take(1)).subscribe((user) => {
              this.analyticsService.trackLoginSucceeded(user?.role);
              void this.navigateByRole(user);
            });
          }
        });
      } catch (error: any) {
        this.loading = false;
        this.analyticsService.trackLoginFailed(error?.code || error?.message || 'login_failed');
        // Failed login attempts are now logged by the backend API when token verification fails
        // No need to log from frontend
        this.snackBar.open(
          error.message || 'Login failed. Please check your credentials',
          'Close',
          { duration: 5000 }
        );
      }
    }
  }

  private async navigateByRole(user: User | null): Promise<void> {
    if (user?.isPlatformAdmin) {
      this.authService.setPreferredAppMode('org');
    }
    if (await this.shouldRouteToTeamBillingSetup(user)) {
      await this.router.navigate(['/billing/plan-selector'], { queryParams: { scope: 'team' } });
      return;
    }
    if (await this.shouldRouteToPlanSelector(user)) {
      await this.router.navigate(['/billing/plan-selector']);
      return;
    }
    if (user?.role === 'admin') {
      await this.router.navigate(['/admin/new-request']);
      return;
    }
    if (user?.role === 'user') {
      await this.router.navigate(['/user/data-request']);
      return;
    }
    await this.router.navigate(['/admin/new-request']);
  }

  async goToForgotPassword(): Promise<void> {
    const rawEmail = String(this.loginForm.get('email')?.value || '').trim();
    const queryParams = rawEmail ? { email: rawEmail } : undefined;
    await this.router.navigate(['/forgot-password'], { queryParams });
  }

  private async shouldRouteToPlanSelector(user: User | null): Promise<boolean> {
    if (user?.isPlatformAdmin) {
      return false;
    }
    try {
      const profile = await firstValueFrom(this.apiService.getCurrentUserProfile());
      const profileUser = profile.user;
      if (user?.role !== 'user') {
        return false;
      }
      return (
        profileUser.enabled === true &&
        profileUser.team !== true &&
        profileUser.approvalStatus === 'APPROVED' &&
        !profileUser.selectedPlanId
      );
    } catch {
      return false;
    }
  }

  private async shouldRouteToTeamBillingSetup(user: User | null): Promise<boolean> {
    if (user?.isPlatformAdmin) {
      return false;
    }
    if (user?.role !== 'admin') {
      return false;
    }
    try {
      const profile = await firstValueFrom(this.apiService.getCurrentUserProfile());
      const profileUser = profile.user;
      return (
        profileUser.enabled === true &&
        profileUser.team === true &&
        profileUser.isBillingOwner === true &&
        profileUser.requiresTeamBillingSetup === true
      );
    } catch {
      return false;
    }
  }

  private async storeCredentialForPasswordManagers(email: string, password: string): Promise<void> {
    if (typeof window === 'undefined' || typeof navigator === 'undefined' || !window.isSecureContext) {
      return;
    }
    const credentialsApi = (navigator as Navigator & {
      credentials?: { store?: (credential: unknown) => Promise<unknown> };
    }).credentials;
    const PasswordCredentialCtor = (window as Window & { PasswordCredential?: new (data: { id: string; password: string; name?: string }) => unknown }).PasswordCredential;
    if (!credentialsApi?.store || typeof PasswordCredentialCtor !== 'function') {
      return;
    }
    try {
      const credential = new PasswordCredentialCtor({
        id: email,
        password,
        name: email,
      });
      await credentialsApi.store(credential);
    } catch {
      // Best-effort only; browser can refuse storage in some contexts.
    }
  }
}


